// Case Data: the two-party input grid with expander rows, alternating shading, drop-down choice values, automatic
// values (asterisk), expand/collapse all and show/hide zero rows. Money is entered in the case period (Monthly/Yearly).
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { Case, FilingStatus, PartyIndex } from '@divorceos/schema';
import { act } from '../actions';
import { useCaseView } from '../caseView';
import { helpFor } from '../help';
import { factor, periodWord } from '../model';
import { FILINGS, ROWS, flattenRows, moneyValue, type FlatLine, type GridRow, type SubRow } from '../rows';
import { useDis } from '../store';
import { childrenWith } from '../../lib/rows';
import { ncpPercentFor } from '../../lib/children';

const NUMERIC = /^-?\d*\.?\d+$/;
const fmtInt = (n: number): string => Math.round(n).toLocaleString('en-US');

/** Text input for a number: shows the formatted value, edits live, reformats on blur.
 *  ENG-02: a blank entry means zero; non-numeric text is flagged and not silently committed as 0. */
function NumCell({ value, onChange, label, decimals = false, cellId }: { value: number; onChange: (n: number) => void; label: string; decimals?: boolean; cellId: string }) {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? (decimals ? String(Math.round(value * 100) / 100) : fmtInt(value));
  const cleaned = text === null ? '' : text.replace(/[$,\s]/g, '');
  const invalid = text !== null && cleaned !== '' && !NUMERIC.test(cleaned);
  return (
    <input data-cell={cellId} inputMode="decimal" aria-label={label} value={shown}
      className={`${!text && Math.round(value * 100) === 0 ? 'z' : ''}${invalid ? ' invalid' : ''}`}
      title={invalid ? 'Enter a number, or leave the cell blank for zero.' : undefined} aria-invalid={invalid || undefined}
      onFocus={(e) => { setText(decimals ? String(Math.round(value * 100) / 100) : String(Math.round(value))); requestAnimationFrame(() => e.target.select()); }}
      onChange={(e) => {
        const t = e.target.value; setText(t);
        const cl = t.replace(/[$,\s]/g, '');
        if (cl === '') { onChange(0); return; }                 // blank → zero
        if (NUMERIC.test(cl)) { const n = parseFloat(cl); if (Number.isFinite(n)) onChange(n); } // valid → commit; invalid → keep prior value
      }}
      onBlur={() => setText(null)} />
  );
}

function TextCell({ value, onChange, label, cellId, type = 'text' }: { value: string; onChange: (s: string) => void; label: string; cellId: string; type?: 'text' | 'date' }) {
  return <input data-cell={cellId} className="txt" type={type} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} />;
}

const EX_MAP = { personal: 'personal', dependent: 'dependent', qualifyingChildrenCtc: 'ctc', qualifyingChildrenEic: 'eic', qualifyingChildrenCcc: 'ccc', childrenUnder6: 'under6', otherDependents: 'otherDependents' } as const;

export function CaseData() {
  const v = useCaseView();
  const { c, labels, nc } = v;
  const update = useDis((s) => s.update);
  const setUi = useDis((s) => s.setUi);
  const setPrefs = useDis((s) => s.setPrefs);
  const sel = useDis((s) => s.ui.sel);
  const tooltips = useDis((s) => s.ui.tooltips);
  const hideZero = useDis((s) => s.ui.hideZeroGrid);
  const expanded = v.doc.prefs.expanded;
  const f = factor(c);
  const tableRef = useRef<HTMLTableElement>(null);

  const isZero = (row: GridRow, sub: SubRow | null) => [0, 1].every((p) => Math.round(moneyValue(c, p as PartyIndex, row, sub) * f) === 0);
  const lines = flattenRows(ROWS, expanded, { hideZero, isZero });
  const visible = lines.filter((l) => l.visible);
  const anyOpen = ROWS.some((r) => r.subs && expanded[r.id]);

  const exTotal = (p: PartyIndex): number => {
    const e = c.parties[p].exemptions;
    if (e.auto && nc) { const x = nc.parties[p].exemptions; return x.personal + x.special + x.newSpouse + x.dependent; }
    return e.personal + e.special + e.newSpouse + e.dependent;
  };

  const cell = (line: FlatLine, p: PartyIndex) => {
    const { row, sub } = line;
    const id = `${p}:${sub?.id ?? row.id}`;
    const lbl = `${sub?.label ?? row.label}, ${labels[p]}`;
    if (sub) {
      const locked = sub.lockedWhen?.(c, p) ?? false;
      if (sub.kind === 'text' || sub.kind === 'date') return <TextCell cellId={id} label={lbl} type={sub.kind === 'date' ? 'date' : 'text'} value={String(sub.get(c, p))} onChange={(s) => update((x) => sub.set(x, p, s))} />;
      if (sub.kind === 'bool') return (
        <select data-cell={id} aria-label={lbl} value={sub.get(c, p) ? 'y' : 'n'} onChange={(e) => update((x) => sub.set(x, p, e.target.value === 'y'))}>
          <option value="y">Yes</option><option value="n">No</option>
        </select>
      );
      if (locked) {
        let shown = Number(sub.get(c, p));
        if (row.id === 'exempt' && nc) { const k = sub.id.replace('ex_', '') as keyof typeof EX_MAP; shown = nc.parties[p].exemptions[EX_MAP[k]]; }
        if (row.id === 'hardship' && v.result) shown = v.result.parties[p].hardshipTotal - c.parties[p].hardship.extraordinaryHealth - c.parties[p].hardship.catastrophicLosses;
        return <span className="ro" title="Automatic value">{sub.kind === 'money' ? fmtInt(shown * f) : fmtInt(shown)}</span>;
      }
      if (sub.kind === 'money') return <NumCell cellId={id} label={lbl} value={Number(sub.get(c, p)) * f} onChange={(n) => update((x) => sub.set(x, p, n / f))} />;
      return <NumCell cellId={id} label={lbl} value={Number(sub.get(c, p))} decimals={sub.kind === 'percent'} onChange={(n) => update((x) => sub.set(x, p, n))} />;
    }
    switch (row.kind) {
      case 'info': return <span className="ro">{c.info.partyNames[p] || '—'}</span>;
      case 'nkids': { const n = childrenWith(c, p); return <span className={`ro go${n ? '' : ' z'}`} onClick={() => setUi({ left: 'children' })}>{n}</span>; }
      case 'ncp': { const t = ncpPercentFor(c.children, c.parties[p].timeWithNcpPercent, p); return <span className={`ro go${t ? '' : ' z'}`} onClick={() => setUi({ left: 'children' })}>{Math.round(t * 10) / 10}%</span>; }
      case 'filing': return (
        <select data-cell={id} aria-label={lbl} value={c.parties[p].filingStatus} onChange={(e) => update((x) => { x.parties[p].filingStatus = e.target.value as FilingStatus; })}>
          {FILINGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      );
      case 'exempt': return <NumCell cellId={id} label={lbl} value={exTotal(p)} onChange={(n) => update((x: Case) => {
        const e = x.parties[p].exemptions;
        if (e.auto && nc) { const r = nc.parties[p].exemptions; Object.assign(e, { personal: r.personal, special: r.special, newSpouse: r.newSpouse, qualifyingChildrenCtc: r.ctc, qualifyingChildrenEic: r.eic, qualifyingChildrenCcc: r.ccc, childrenUnder6: r.under6, otherDependents: r.otherDependents }); }
        e.auto = false;
        e.dependent = Math.max(0, Math.round(n) - e.personal - e.special - e.newSpouse);
      })} />;
      case 'money': return row.get && row.set ? <NumCell cellId={id} label={lbl} value={row.get(c.parties[p]) * f} onChange={(n) => update((x) => row.set?.(x.parties[p], n / f))} /> : null;
      case 'composite': { const t = row.total?.(c, p) ?? 0; return <span className={`ro${Math.round(t * f) ? '' : ' z'}`}>{fmtInt(t * f)}</span>; }
      default: return null;
    }
  };

  /** ↑/↓/Enter move between visible rows, keeping the party column when typing. */
  const onKeyDown = (e: KeyboardEvent<HTMLTableElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
    const t = e.target as HTMLElement;
    if (t.tagName === 'SELECT' && e.key !== 'Enter') return;
    const idx = visible.findIndex((l) => (l.sub?.id ?? l.row.id) === sel);
    const next = visible[Math.max(0, Math.min(visible.length - 1, idx + (e.key === 'ArrowUp' ? -1 : 1)))];
    if (!next) return;
    e.preventDefault();
    const nid = next.sub?.id ?? next.row.id;
    setUi({ sel: nid, helpTopic: null });
    const col = (t.getAttribute('data-cell') ?? '0:').split(':')[0];
    requestAnimationFrame(() => {
      const el = tableRef.current?.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-cell="${col}:${nid}"]`);
      el?.focus();
    });
  };

  // Keep the selected row in view when it changes from the keyboard.
  useEffect(() => { tableRef.current?.querySelector('tr.sel')?.scrollIntoView({ block: 'nearest' }); }, [sel]);

  return (
    <>
      <div className="gridbar">
        <span>{periodWord(c)} amounts · tax year {c.taxYear}</span>
        <span className="sp" />
        <button type="button" className="lnkbtn" title="Z" onClick={() => act('toggleZero')}>{hideZero ? 'Show zero rows' : 'Hide zero rows'}</button>
        <button type="button" className="lnkbtn" title="X" onClick={() => act(anyOpen ? 'collapseAll' : 'expandAll')}>{anyOpen ? 'Collapse all' : 'Expand all'}</button>
      </div>
      <table className="grid" id="grid" ref={tableRef} onKeyDown={onKeyDown}>
        <colgroup><col className="c-l" /><col /><col /></colgroup>
        <thead><tr><th scope="col">Input</th><th scope="col" className="pa">{labels[0]}</th><th scope="col" className="pb">{labels[1]}</th></tr></thead>
        <tbody>
          {lines.map((line) => {
            if (!line.visible) return null;
            const { row, sub } = line;
            const id = sub?.id ?? row.id;
            const cls = [sub ? 'sub' : '', line.shade % 2 ? 'alt' : '', sel === id ? 'sel' : ''].filter(Boolean).join(' ');
            const help = tooltips ? helpFor(id)?.[0] : undefined;
            const auto = (p: PartyIndex) => (!sub && row.auto?.(c, p) ? ' auto' : '');
            const isOpen = !!expanded[row.id];
            return (
              <tr key={id} data-row={id} className={cls} onMouseDown={() => { if (sel !== id) setUi({ sel: id, helpTopic: null }); }} onFocus={() => { if (sel !== id) setUi({ sel: id, helpTopic: null }); }}>
                <td className="l" title={help}>
                  {!sub && (row.subs
                    ? <button type="button" className="tri" aria-expanded={isOpen} aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${row.label}`} onClick={() => setPrefs((pr) => { pr.expanded[row.id] = !isOpen; })}>{isOpen ? '▼' : '▶'}</button>
                    : <span className="tri ph" />)}
                  {sub?.label ?? row.label}
                </td>
                <td className={`v${auto(0)}`}>{cell(line, 0)}</td>
                <td className={`v${auto(1)}`}>{cell(line, 1)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ padding: '8px 12px', color: 'var(--muted)', fontSize: 11.5 }}>
        * automatic value. ▶ rows expand to their detail lines. Choose a row to see its help on the Help tab.{hideZero ? ' Zero rows are hidden (Z shows them).' : ''}
      </div>
    </>
  );
}
